import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { clsx } from 'clsx';
import { useChatLayout } from '../../components/layout/ChatLayout';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AnomalyCard } from '../../components/pulse/AnomalyCard';
import { pulseApi, type PulseSeverity, type PulseAnomalyCard } from '../../services/pulse-api';
import { PulseHeader, PulseTabs } from './PulseAccountsPage';

const SEVERITY_FILTERS: Array<{ label: string; value: PulseSeverity | null; pillClass: string }> = [
  { label: 'All', value: null, pillClass: 'bg-foreground text-background' },
  { label: 'ALERT!', value: 'ALERT!', pillClass: 'text-destructive hover:bg-destructive/15' },
  { label: 'Dangerous', value: 'Dangerous', pillClass: 'text-alert hover:bg-alert/15' },
  { label: 'Caution', value: 'Caution', pillClass: 'text-warn hover:bg-warn/15' },
];

/**
 * Pulse — anomaly feed tab. Spans every business in the workspace (R11),
 * filterable by severity and business, day-grouped, paginated.
 */
const PulseFeedPage: React.FC = () => {
  const { isMobile, toggleSidebar } = useChatLayout();
  const [severity, setSeverity] = useState<PulseSeverity | null>(null);
  const [orgFilter, setOrgFilter] = useState<string | null>(null);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['pulse', 'anomalies', { severity, orgFilter }],
      queryFn: ({ pageParam }) =>
        pulseApi.listAnomalies({
          cursor: pageParam,
          severity,
          organizationId: orgFilter,
          limit: 20,
        }),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const grouped = groupByDay(items);

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl mx-auto space-y-6">
        <PulseHeader />
        <PulseTabs current="feed" />

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-white dark:bg-white/5 border border-border rounded-lg p-0.5">
            {SEVERITY_FILTERS.map((f) => {
              const active = severity === f.value;
              return (
                <button
                  key={f.label}
                  onClick={() => setSeverity(f.value)}
                  className={clsx(
                    'text-xs font-medium px-2.5 py-1 rounded-md transition-colors',
                    active ? f.pillClass : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <BusinessFilter value={orgFilter} onChange={setOrgFilter} />
        </div>

        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-destructive/15 border border-destructive/20 text-destructive p-4">
            Failed to load the feed. Try again in a moment.
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="rounded-xl bg-muted border border-border p-8 text-center">
            <p className="text-muted-foreground">No anomalies in this view.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Pulse will surface them here as soon as it detects something.
            </p>
          </div>
        )}

        {grouped.map(({ dayLabel, cards }) => (
          <section key={dayLabel} className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {dayLabel}
            </div>
            <div className="space-y-3">
              {cards.map((c) => (
                <AnomalyCard key={c.resultId} card={c} showAccount />
              ))}
            </div>
          </section>
        ))}

        {hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg py-2.5 disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
};

function groupByDay(cards: PulseAnomalyCard[]): Array<{ dayLabel: string; cards: PulseAnomalyCard[] }> {
  const groups = new Map<string, PulseAnomalyCard[]>();
  for (const card of cards) {
    const key = dayLabel(card.detectedAt);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(card);
  }
  return [...groups.entries()].map(([dayLabel, cards]) => ({ dayLabel, cards }));
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfToday - startOfDate) / 86_400_000);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default PulseFeedPage;
